import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    switchMap,
    takeUntil,
} from 'rxjs/operators';

import { CommitmentService } from '../../coaching-commitment/services/commitment.service';
import { catchErrorLogRet } from '../../common/rest.operators';
import { Dependencies } from '../../store';
import { PayloadAction } from '../../store.model';
import { UserService } from '../../user/user.service';

import {
    ActionType,
    Reload,
    reloadResult,
    ReloadUser,
    reloadUserResult,
} from './pillar-one.action';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reload = (
    action: ActionsObservable<PayloadAction<ActionType, Reload>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__P1__RELOAD),
        switchMap((act) => {
            const service =
                deps.injector.get<CommitmentService>(CommitmentService);
            return of(null).pipe(
                service.retrievePerformerOa(() => ({
                    performerId: act.payload.userId,
                    periodType: act.payload.periodTypeId,
                })),
                map((ret) => {
                    return reloadResult(
                        {
                            ...act.payload,
                            performerOa: ret,
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.COACHING_DETAIL__P1__RELOAD_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reloadUser = (
    action: ActionsObservable<PayloadAction<ActionType, ReloadUser>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__P1__RELOAD_USER),
        switchMap((act) => {
            const service = deps.injector.get<UserService>(UserService);
            return of(null).pipe(
                concatMap(() => {
                    return forkJoin([
                        service
                            .retrieve(act.payload.userId)
                            .pipe(catchErrorLogRet({})),
                        service
                            .listHierarchy('managers', act.payload.userId)
                            .pipe(
                                catchErrorLogRet([]),
                                map((users) =>
                                    !users.length ? null : users[0],
                                ),
                            ),
                    ]);
                }),
                map((ret) => {
                    return reloadUserResult(
                        {
                            ...act.payload,
                            user: ret[0],
                            userManager: ret[1],
                        },
                        act.storePath,
                    );
                }),
                catchError(() =>
                    of(reloadUserResult({ ...act.payload }, act.storePath)),
                ),
                takeUntil(
                    action.pipe(
                        ofType(
                            ActionType.COACHING_DETAIL__P1__RELOAD_USER_CANCEL,
                        ),
                        filter(
                            (cancelAct) =>
                                cancelAct.storePath === act.storePath,
                        ),
                    ),
                ),
            );
        }),
    );

export const coachingDetailPillarOneEpics = [reload, reloadUser];
